import { MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import FormInputComponent from "@via/components/inputs/form-input-component";
import PasswordInput from "@via/components/password-input/password-input.tsx";
import Modal from "@via/components/modal.tsx";
import SelectComponent from "@via/components/select/select";
import { useLaunchWorkbench } from "@via/pages/workbenchs/workbenchs.api";
import { ModalSize, ROUTES } from "@via/utils/constants.tsx";
import { launcWorkbenchOptions } from "@via/utils/form-validators";
import { WorkbenchInstanceState } from "common";
import { useState, type FC } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

interface LaunchWorkbenchModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  workbenchId: number;
}

const instanceTypeOptions = [
  {
    label: "n2-highmem-2 (2 vCPUs, 16GB RAM)",
    value: "n2-highmem-2",
  },
  {
    label: "n2-highmem-4 (4 vCPUs, 32GB RAM)",
    value: "n2-highmem-4",
  },
  {
    label: "n2-highmem-8 (8 vCPUs, 64GB RAM)",
    value: "n2-highmem-8",
  },
  {
    label: "n2-highmem-16 (16 vCPUs, 128GB RAM)",
    value: "n2-highmem-16",
  },
  {
    label: "n2-highmem-32 (32 vCPUs, 256GB RAM)",
    value: "n2-highmem-32",
  },
  {
    label: "n2-highmem-48 (48 vCPUs, 384GB RAM)",
    value: "n2-highmem-48",
  },
  {
    label: "n2-standard-4 (4 vCPUs, 16GB RAM)",
    value: "n2-standard-4",
  },
  {
    label: "n2-standard-8 (8 vCPUs, 32GB RAM)",
    value: "n2-standard-8",
  },
  {
    label: "n2-standard-16 (16 vCPUs, 64GB RAM)",
    value: "n2-standard-16",
  },
  {
    label: "n2-standard-32 (32 vCPUs, 128GB RAM)",
    value: "n2-standard-32",
  },
  {
    label: "n2-standard-48 (48 vCPUs, 192GB RAM)",
    value: "n2-standard-48",
  },
  {
    label: "n2-standard-64 (64 vCPUs, 256GB RAM)",
    value: "n2-standard-64",
  },
  {
    label: "n2-standard-80 (80 vCPUs, 320GB RAM)",
    value: "n2-standard-80",
  },
  {
    label: "n2-standard-96 (96 vCPUs, 384GB RAM)",
    value: "n2-standard-96",
  },
];
const DEFAULT_INSTANCE_TYPE_OPTION = "n2-standard-4";
const DEFAULT_DISK_SIZE = 100;

const LaunchWorkbenchModal: FC<LaunchWorkbenchModalProps> = ({
  open,
  setOpen,
  workbenchId,
}) => {
  const navigate = useNavigate();
  const [showDefaultFields, setShowDefaultFields] = useState(false);
  const [showPasswordOptions, setShowPasswordOptions] = useState(false);
  const [isSystemGeneratedPassword, setIsSystemGeneratedPassword] =
    useState(true);

  const { mutateAsync: launchWorkbench, isPending: isLaunchWorkbenchLoading } =
    useLaunchWorkbench();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      instanceType: DEFAULT_INSTANCE_TYPE_OPTION,
      diskSize: DEFAULT_DISK_SIZE,
      password: "",
    },
  });

  const handleCancel = () => {
    setOpen(false);
  };

  // Resolving true hands the close to Modal. Invalid fields resolve false and keep
  // the dialog open, and a failed launch rejects, which also keeps it open with the
  // mutation's notification on screen. The navigation moved here from a success
  // effect so it uses the awaited result rather than a data field arriving later.
  const handleConfirmModal = async (): Promise<boolean> => {
    let launched = false;
    await handleSubmit(async data => {
      const { state, accessUrl } = await launchWorkbench({
        payload: data,
        workbenchId,
      });
      if (state === WorkbenchInstanceState.Running) {
        window.open(accessUrl, "_blank", "noreferrer");
      }
      navigate({
        pathname: ROUTES.WORKBENCH_MANAGE_STATUS,
      });
      launched = true;
    })();
    return launched;
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Launch Workbench"
      confirmText="Launch"
      size={ModalSize.Large}
      onCancel={handleCancel}
      isConfirmLoading={isLaunchWorkbenchLoading}
      onConfirm={handleConfirmModal}>
      <form
        onSubmit={e => {
          e.preventDefault();
          e.stopPropagation();
        }}>
        <div>
          <div className="my-6 flex w-full flex-col">
            <label className="flex w-36 flex-row items-center justify-between text-sm font-medium leading-6 text-gray-900">
              Name
            </label>
            <div className="w-full">
              <FormInputComponent
                control={control}
                name="name"
                placeholder="Enter a Workbench Name"
              />
            </div>
          </div>
          <div className="flex flex-row items-center">
            <label className="mr-2 block text-sm font-medium leading-6 text-gray-900">
              Advanced Options
            </label>
            <div onClick={() => setShowDefaultFields(!showDefaultFields)}>
              {showDefaultFields ? (
                <MinusCircleIcon className="h-5 w-5 cursor-pointer text-indigo-600 hover:text-indigo-500" />
              ) : (
                <PlusCircleIcon className="h-5 w-5 cursor-pointer text-indigo-600 hover:text-indigo-500" />
              )}
            </div>
          </div>
          {showDefaultFields && (
            <div className="my-6 flex w-full flex-col">
              <label className="flex w-36 flex-row items-center justify-between text-sm font-medium leading-6 text-gray-900">
                Instance Type
              </label>
              <div className="w-full my-2">
                <SelectComponent
                  control={control}
                  name="instanceType"
                  rules={launcWorkbenchOptions.instanceType}
                  options={instanceTypeOptions}
                  placeholder="Choose GCP Instance Type"
                />
                {errors?.instanceType && (
                  <span className="text-xs text-rose-500">
                    {errors.instanceType.message}
                  </span>
                )}
              </div>
              <FormInputComponent
                control={control}
                name="diskSize"
                label="Disk Size (GB)"
                type="number"
                min={100}
                max={4096}
                placeholder="GB"
                validations={launcWorkbenchOptions.diskSize}
                onKeyDown={e => {
                  if (e.key === "e" || e.key === "E" || e.key === "+") {
                    e.preventDefault();
                  }
                }}
              />
            </div>
          )}
          <div className="flex flex-row items-center mt-4">
            <label className="mr-2 block text-sm font-medium leading-6 text-gray-900">
              Password Options
            </label>
            <div onClick={() => setShowPasswordOptions(!showPasswordOptions)}>
              {showPasswordOptions ? (
                <MinusCircleIcon className="h-5 w-5 cursor-pointer text-indigo-600 hover:text-indigo-500" />
              ) : (
                <PlusCircleIcon className="h-5 w-5 cursor-pointer text-indigo-600 hover:text-indigo-500" />
              )}
            </div>
          </div>
          {showPasswordOptions && (
            <div className="my-6 flex w-full flex-col">
              <label className="flex flex-row items-center justify-between mb-4 text-sm">
                You can choose to set a password for your workbench or let the
                system generate one for you.
              </label>
              <div className="flex flex-row items-center space-x-4 my-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="passwordOption"
                    value="systemGenerated"
                    checked={isSystemGeneratedPassword}
                    onChange={() => {
                      setValue("password", "");
                      setIsSystemGeneratedPassword(true);
                    }}
                    className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  />
                  <span className="text-sm text-gray-900">
                    System Generated
                  </span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="passwordOption"
                    value="userDefined"
                    checked={!isSystemGeneratedPassword}
                    onChange={() => setIsSystemGeneratedPassword(false)}
                    className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  />
                  <span className="text-sm text-gray-900">User Defined</span>
                </label>
              </div>
              {!isSystemGeneratedPassword && (
                <div className="w-full my-2">
                  <div className="relative">
                    <PasswordInput
                      register={register}
                      registerValue={"password"}
                      placeholder="Enter Password"
                      watch={watch}
                      errors={errors}
                      hasCopyButton
                      focusRingWidth="ring-1"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default LaunchWorkbenchModal;
